import { useCallbackRef } from '@mantine/hooks';
import React, { useMemo } from 'react';
import Markdown, {
  Components,
  defaultUrlTransform,
  UrlTransform,
} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatCodeBlock } from 'src/app/chat/ChatCodeBlock';
import { EntityRefLink } from 'src/app/chat/EntityRefLink';
import { isEntityRef } from 'src/app/chat/entityRefs';
import { stripMemoryMarkers } from 'src/app/chat/memoryMarkers';
import { Anchor } from 'src/design/atoms/Anchor';
import { Box } from 'src/design/atoms/Box';
import classes from './chat.module.css';

export interface IChatMarkdownProps {
  text: string;
  /** Hides a `[memory:N]` marker still cut off at the end of the text. */
  streaming?: boolean;
  onRefClick?: () => void;
}

const remarkPlugins = [remarkGfm];

const urlTransform: UrlTransform = (url, key) =>
  key === 'href' && isEntityRef(url) ? url : defaultUrlTransform(url);

const buildComponents = (onRefClick: () => void): Components => ({
  pre: (props) => <ChatCodeBlock {...props} />,
  table: ({ node: _node, ref: _ref, ...props }) => (
    <Box className={classes.markdownTableScroll} data-markdown-table-scroll>
      <Box component="table" {...props} />
    </Box>
  ),
  a: ({ node: _node, ref: _ref, href, children, ...props }) => {
    if (!href) return <>{children}</>;
    if (!isEntityRef(href)) {
      return (
        <Anchor href={href} {...props}>
          {children}
        </Anchor>
      );
    }
    return (
      <EntityRefLink href={href} onRefClick={onRefClick}>
        {children}
      </EntityRefLink>
    );
  },
});

/** @registry Renders assistant markdown (GFM, no raw HTML) with a copy button on code blocks, tables that scroll sideways instead of widening the bubble, `ref:` entity links as in-app router links, and `[memory:N]` markers removed. */
export const ChatMarkdown: React.FC<IChatMarkdownProps> = ({
  text,
  streaming,
  onRefClick,
}) => {
  const handleRefClick = useCallbackRef(onRefClick);
  const components = useMemo(
    () => buildComponents(handleRefClick),
    [handleRefClick],
  );
  return (
    <Box className={classes.markdown}>
      <Markdown
        remarkPlugins={remarkPlugins}
        components={components}
        urlTransform={urlTransform}
      >
        {stripMemoryMarkers(text, streaming)}
      </Markdown>
    </Box>
  );
};
